import dayjs from 'dayjs'
import { Activity, ActivityAgent, ActivityType } from '../../../api/activities/types'
import { Contact } from '../../../api/contacts/types'
import { ContactPersonCompany } from '../../../api/contacts/types/response'
import { ProjectItem } from '../../../api/projects/types'
import { Property } from '../../../api/properties/types'
import { activityResponseFields } from '../../../common/response/activity'
import { ActivityFormValues, ActivityModalData } from './types'

// Creates basic date/time structure for a new activity
export const createActivityDateTime = (date: Date | null = null) => {
    const now = dayjs()
    const dateTimeFrom = date ? dayjs(date).hour(now.hour()).minute(now.minute()) : now
    const dateTimeTo = dateTimeFrom.add(15, 'minute')

    return {
        [activityResponseFields.DATE_FROM]: dateTimeFrom.format('YYYY-MM-DD'),
        [activityResponseFields.DATE_TO]: dateTimeFrom.format('YYYY-MM-DD'),
        [activityResponseFields.TIME_FROM]: dateTimeFrom.format('HH:mm'),
        [activityResponseFields.TIME_TO]: dateTimeTo.format('HH:mm'),
    }
}

/**
 * Sets activity data for modal form to avoid bloating redux with unnecessary data
 * @param activity - Raw activity object from API/store with deeply nested data
 * @returns Cleaned activity object with simplified structures for modal inputs data display
 */
export const setActivityModalData = (activity: Activity) => {
    return {
        id: activity.id,
        title: activity.title,
        notes: activity.notes,
        location: activity.location,
        completed: activity.completed,

        dateFrom: activity.dateFrom,
        dateTo: activity.dateTo,
        timeFrom: activity.timeFrom,
        timeTo: activity.timeTo,
        durationDay: activity.durationDay,

        activityType: activity.activityType,

        activityAgent: activity.activityAgent?.map((item: ActivityAgent) => ({
            agent: {
                id: item.agent?.id,
                firstName: item.agent?.firstName,
                lastName: item.agent?.lastName,
                agencyOffice: {
                    id: item.agent?.agencyOffice?.id,
                },
            },
            notificationSend: item.notificationSend,
        })),

        // Contact - minimal data for contact info display/permissions
        contact: activity.contact
            ? {
                  id: activity.contact.id,
                  contactType: activity.contact.contactType,
                  contactPerson: {
                      firstName: activity.contact.contactPerson?.firstName,
                      lastName: activity.contact.contactPerson?.lastName,
                  },
                  contactCompany: activity.contact.contactCompany?.name ? { name: activity.contact.contactCompany.name } : undefined,
                  agent: {
                      id: activity.contact.agent?.id,
                      agencyOffice: {
                          id: activity.contact.agent?.agencyOffice?.id,
                      },
                  },
                  contactPersonCompany: activity.contact.contactPersonCompany?.map((cpc: ContactPersonCompany) => ({
                      company: {
                          agent: {
                              id: cpc.company?.agent?.id,
                              agencyOffice: {
                                  id: cpc.company?.agent?.agencyOffice?.id,
                              },
                          },
                      },
                  })),
              }
            : null,

        // Contact companies - minimal data for links/display/permissions
        contactCompany: activity.contactCompany?.map((item: Contact) => ({
            id: item.id,
            agent: {
                id: item.agent?.id,
                agencyOffice: {
                    id: item.agent?.agencyOffice?.id,
                },
            },
            // Standalone company
            contactCompany: item.contactCompany?.name
                ? {
                      name: item.contactCompany.name,
                  }
                : undefined,
            // Company related to person (contact)
            contactPersonCompany: item.contactPersonCompany?.map((cpc: ContactPersonCompany) => ({
                company: {
                    agent: {
                        id: cpc.company?.agent?.id,
                        agencyOffice: {
                            id: cpc.company?.agent?.agencyOffice?.id,
                        },
                    },
                    contactCompany: {
                        name: cpc.company?.contactCompany?.name,
                    },
                },
            })),
        })),

        property: activity.property?.map((item: Property) => ({
            id: item.id,
            internalAppId: item.internalAppId,
            propertyService: item.propertyService?.name,
            propertyType: item.propertyType?.name,
            translations: item.translations,
            street: item.street,
            streetNumber: item.streetNumber,
            city: item.city,
            country: item.country,
            agent: {
                id: item.agent?.[0]?.id,
                agencyOffice: {
                    id: item.agent?.[0]?.agencyOffice?.id,
                },
            },
        })),
        project: activity.project?.map((item: ProjectItem) => ({
            id: item.id,
            internalAppId: item.internalAppId,
            projectTitle: item.translations?.[item.currentLocale]?.projectTitle,
            translations: item.translations,
            street: item.street,
            streetNumber: item.streetNumber,
            city: item.city,
            country: item.country,
            agent: {
                id: item.agent?.[0]?.id,
                agencyOffice: {
                    id: item.agent?.[0]?.agencyOffice?.id,
                },
            },
        })),

        activityReminder: activity.activityReminder,
    }
}

/**
 * Syncs formik values to event structure for modal props
 * Maps snake_case formik fields to camelCase event structure with nested objects
 * TODO: see if this can be done in better way instead of updating redux store with formik data (also goddamn snake_case vs camelCase)
 */
export const syncFormikToEvent = (formikValues: ActivityFormValues, currentEvent: ActivityModalData, selectedType: ActivityType) => {
    return {
        ...currentEvent,

        id: formikValues.id ?? currentEvent.id,
        title: formikValues.title ?? currentEvent.title,
        notes: formikValues.notes ?? currentEvent.notes,
        location: formikValues.location ?? currentEvent.location,
        completed: formikValues.completed ?? currentEvent.completed,

        dateFrom: formikValues.date_from ?? currentEvent.dateFrom,
        timeFrom: formikValues.time_from ?? currentEvent.timeFrom,
        dateTo: formikValues.date_to ?? currentEvent.dateTo,
        timeTo: formikValues.time_to ?? currentEvent.timeTo,
        durationDay: formikValues.duration_day ? 1 : 0,

        activityType: selectedType,

        // Contact - only need id for validation
        contact: formikValues.contact_id ? { id: formikValues.contact_id } : currentEvent.contact,

        // Contact company collection
        contactCompany: formikValues.contact_company_collection?.map((c) => ({ id: c.contact_id })) ?? currentEvent.contactCompany,

        property: formikValues.property_id?.length ? formikValues.property_id.map((id) => ({ id })) : currentEvent.property,
        project: formikValues.project_id?.length ? formikValues.project_id.map((id) => ({ id })) : currentEvent.project,

        activityAgent:
            formikValues.activity_agent_group?.map((item) => {
                const existingAgent = currentEvent.activityAgent?.find((a) => a.agent?.id?.toString() === item.agent_id?.toString())
                return {
                    agent: {
                        ...(existingAgent?.agent ?? {}),
                        id: item.agent_id,
                    },
                    notificationSend: item.notification_send ? 1 : 0,
                }
            }) ?? currentEvent.activityAgent,

        activityReminder:
            formikValues.activity_reminder_group
                ?.filter((item) => item.channel && item.reminder_minutes_before)
                .map((item) => ({
                    channel: item.channel,
                    reminderMinutesBefore: Number(item.reminder_minutes_before),
                })) ?? currentEvent.activityReminder,
    }
}
